import React, { useEffect, useState, useRef, useCallback } from "react";
import ExotelCRMWebSDK from "exotel-ip-calling-crm-websdk";
import "./CallControlRibbon.css";
import { normalizeUserFacingMessage, userFacingApiMessage } from "./userFacingMessages";
// Transcription disabled for deployment
// import AudioCaptureService from "./services/audioCapture";
// import TranscriptPanel from "./components/TranscriptPanel";

/**
 * Call Control Ribbon - A reusable component for CRM integration
 * 
 * Props:
 * - config: { accessToken, userId, userMappingVerified? } - Calling credentials (userId must match Exotel app user)
 * - customerData: { phoneNumber, name, email, customerId } - Customer information
 * - position: 'top' | 'bottom' | 'floating' - Ribbon position
 * - onCallEvent: (event, data) => {} - Callback for call events
 * - onReady: () => {} - Callback when ribbon is ready
 */
const CallControlRibbon = ({
  config,
  customerData = {},
  position = 'bottom',
  onCallEvent,
  onReady,
  minimized: initialMinimized = false,
  apiUrl = null,
  apiKey = null
}) => {
  // ALL STATE DECLARATIONS FIRST - Must be before any useEffect hooks
  const [phoneNumber, setPhoneNumber] = useState(customerData?.phoneNumber || "");
  const [isCallActive, setCallActive] = useState(false);
  const [isDeviceRegistered, setIsDeviceRegistered] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isIncomingCall, setIsIncomingCall] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("info");
  const [isDialing, setIsDialing] = useState(false);
  const [callPhase, setCallPhase] = useState("idle");
  const [currentCall, setCurrentCall] = useState(null);
  const [minimized, setMinimized] = useState(initialMinimized);
  const [callDuration, setCallDuration] = useState(0);
  const [showDTMF, setShowDTMF] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [previousSummaries, setPreviousSummaries] = useState([]);
  const [loadingSummaries, setLoadingSummaries] = useState(false);
  const [showPreviousCalls, setShowPreviousCalls] = useState(true);
  const [isExotelAuthInvalid, setIsExotelAuthInvalid] = useState(false);
  
  // Transcription state - DISABLED
  // const [showTranscriptPanel, setShowTranscriptPanel] = useState(false);
  // const [transcripts, setTranscripts] = useState([]);
  // const [currentSummary, setCurrentSummary] = useState("");
  // const [isTranscribing, setIsTranscribing] = useState(false);
  
  // Debug: Log state changes
  useEffect(() => {
    console.log("[CallControlRibbon] isDeviceRegistered changed to:", isDeviceRegistered);
  }, [isDeviceRegistered]);

  // Prevent widget from being minimized during active calls
  useEffect(() => {
    if ((isCallActive || isIncomingCall) && minimized) {
      console.log('[CallControlRibbon] Preventing minimize during call - expanding widget');
      setMinimized(false);
    }
  }, [isCallActive, isIncomingCall, minimized]);

  useEffect(() => {
    const onExternalNotification = (event) => {
      const detail = event?.detail || {};
      const message = String(detail.message || "").trim();
      const type = detail.type || "error";
      if (!message) return;
      showNotificationMessage(message, type);
    };

    window.addEventListener("intalksai:ribbon-notification", onExternalNotification);
    return () => window.removeEventListener("intalksai:ribbon-notification", onExternalNotification);
  }, []);

  useEffect(() => {
    const toFriendlyCallRuntimeMessage = (input) => {
      const text =
        input && typeof input === "object"
          ? String(input.message || input.Error || input.error || "").trim()
          : String(input ?? "").trim();
      return normalizeUserFacingMessage(text);
    };

    const onUnhandledRejection = (event) => {
      const message = toFriendlyCallRuntimeMessage(event?.reason);
      if (message) {
        showNotificationMessage(message, "error");
      }
    };

    const onWindowError = (event) => {
      const message = toFriendlyCallRuntimeMessage(event?.message || event?.error?.message);
      if (message && /exotel|call|sip|webrtc|microphone|authorization|token|permission/i.test(String(event?.message || ""))) {
        showNotificationMessage(message, "error");
      }
    };

    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener("error", onWindowError);
    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      window.removeEventListener("error", onWindowError);
    };
  }, []);

  // Debug: Log config on mount
  useEffect(() => {
    console.log("====================================================================");
    console.log("[CallControlRibbon] INITIALIZATION DEBUG INFO");
    console.log("[CallControlRibbon] apiUrl:", apiUrl || "(not set - summaries will not load)");
    console.log("[CallControlRibbon] apiKey:", apiKey ? "***" + (apiKey.slice(-4) || "") : "(not set)");
    console.log("[CallControlRibbon] accessToken:", config?.accessToken?.substring(0, 20) + "...");
    console.log("[CallControlRibbon] userId:", config?.userId);
    console.log("====================================================================");
  }, [config, apiUrl, apiKey]);
  
  const webPhone = useRef(null);
  const ribbonRef = useRef(null);
  const callTimer = useRef(null);
  const notificationTimerRef = useRef(null);
  const outgoingConnectTimeoutRef = useRef(null);
  const activeCallWatchdogRef = useRef(null);
  const lastToastRef = useRef({ message: "", time: 0 });
  const lastMuteClick = useRef(0);
  const lastHoldClick = useRef(0);
  const exotelAuthErrorShownAtRef = useRef(0);
  const originalFetchRef = useRef(null);
  const originalXhrOpenRef = useRef(null);
  const originalXhrSendRef = useRef(null);
  // const audioCapture = useRef(null); // Transcription disabled

  // Update phone number only when user selects a different customer - don't overwrite during calls
  const lastSyncedCustomerId = useRef(null);
  useEffect(() => {
    if (isIncomingCall || isCallActive) return;
    const cid = customerData?.customerId ?? customerData?.phoneNumber;
    if (customerData?.phoneNumber && cid !== lastSyncedCustomerId.current) {
      lastSyncedCustomerId.current = cid;
      setPhoneNumber(customerData.phoneNumber);
    }
  }, [customerData?.customerId, customerData?.phoneNumber, isIncomingCall, isCallActive]);

  const fetchSummariesTimerRef = useRef(null);

  // Helper: normalize phone to digits, return true if 10+ digits
  const isPhoneValid = (p) => {
    const digits = String(p || '').replace(/\D/g, '');
    return digits.length >= 10;
  };

  // Fetch last 5 call summaries by phone
  const fetchSummariesByPhone = React.useCallback(async (phone) => {
    const p = String(phone || '').trim();
    if (!p || !isPhoneValid(p)) return;
    if (!apiUrl || !apiKey) {
      console.warn('[CallControlRibbon] Cannot fetch summaries: missing apiUrl or apiKey');
      return;
    }
    setLoadingSummaries(true);
    try {
      const url = `${apiUrl}/api/ribbon/summaries/by-phone?phone=${encodeURIComponent(p)}&limit=5`;
      console.log('[CallControlRibbon] Fetching summaries:', url);
      const res = await fetch(url, { headers: { 'x-api-key': apiKey } });
      if (res.ok) {
        const data = await res.json();
        setPreviousSummaries(data.summaries || []);
      } else {
        const errorPayload = await res.json().catch(() => ({}));
        const friendlyMessage = userFacingApiMessage(
          res.status,
          errorPayload,
          "Could not load call history. Please try again."
        );
        showNotificationMessage(friendlyMessage, "warning");
        setPreviousSummaries([]);
      }
    } catch (err) {
      console.warn('[CallControlRibbon] Failed to fetch previous summaries:', err);
      showNotificationMessage("Could not load call history. Please try again.", "warning");
      setPreviousSummaries([]);
    } finally {
      setLoadingSummaries(false);
    }
  }, [apiUrl, apiKey]);

  // Fetch summaries - always use widget input (phoneNumber), never customerData
  useEffect(() => {
    if (isDialing || isIncomingCall || isCallActive) {
      if (fetchSummariesTimerRef.current) {
        clearTimeout(fetchSummariesTimerRef.current);
        fetchSummariesTimerRef.current = null;
      }
      return;
    }
    const phone = String(phoneNumber || '').trim();
    if (!phone || !isPhoneValid(phone)) {
      setPreviousSummaries([]);
      return;
    }
    if (!apiUrl || !apiKey) {
      console.warn('[CallControlRibbon] Skipping summaries fetch: apiUrl or apiKey not set');
      return;
    }
    // Debounce: 400ms after last change when user is typing
    if (fetchSummariesTimerRef.current) clearTimeout(fetchSummariesTimerRef.current);
    fetchSummariesTimerRef.current = setTimeout(() => {
      fetchSummariesTimerRef.current = null;
      fetchSummariesByPhone(phone);
    }, 400);
    return () => {
      if (fetchSummariesTimerRef.current) clearTimeout(fetchSummariesTimerRef.current);
    };
  }, [phoneNumber, apiUrl, apiKey, fetchSummariesByPhone, isDialing, isIncomingCall, isCallActive]);

  const HandleCallEvents = (eventType, ...args) => {
    console.log("[CallControlRibbon] HandleCallEvents", eventType, { args });
    const callData = args[0];
    const rawReason = args[1];

    const extractReasonMessage = (data, fallbackReason) => {
      const parseMaybeJsonMessage = (value) => {
        if (typeof value !== "string") return value;
        const trimmed = value.trim();
        if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return value;
        try {
          const parsed = JSON.parse(trimmed);
          return parsed?.message || parsed?.error || value;
        } catch (_e) {
          return value;
        }
      };

      const candidate = [
        fallbackReason,
        data?.reason,
        data?.Reason,
        data?.error,
        data?.Error,
        data?.message,
        data?.Message,
        data?.description,
        data?.Description,
        data?.cause,
        data?.Cause,
        data?.CallStatus,
        data?.CallState
      ].find((value) => typeof value === "string" && value.trim().length > 0);

      if (!candidate) return "";

      const cleaned = String(parseMaybeJsonMessage(candidate))
        .replace(/^make_call_api_error\s*[:;-]?\s*/i, "")
        .replace(/^error\s*[:;-]?\s*/i, "")
        .trim();

      return cleaned;
    };

    // Notify parent application
    onCallEvent?.(eventType, {
      ...callData,
      customerData,
      timestamp: new Date().toISOString()
    });

    switch (eventType) {
      case "incoming":
        setIsIncomingCall(true);
        setCurrentCall(callData);
        showNotificationMessage("Incoming call.", "info");
        break;
      case "connected":
        setCallActive(true);
        setIsIncomingCall(false);
        setCurrentCall(callData);
        startCallTimer();
        showNotificationMessage("Call connected.", "success");
        
        // Ensure widget is visible when call connects
        if (minimized) {
          setMinimized(false);
        }
        
        // Transcription disabled for deployment
        // Transcription initialization code commented out
        break;
      case "callEnded":
        setCallActive(false);
        setIsIncomingCall(false);
        setIsMuted(false);
        setIsOnHold(false);
        setCurrentCall(null);
        stopCallTimer();
        {
          const reason = extractReasonMessage(callData, rawReason);
          const friendly = normalizeUserFacingMessage(reason);
          showNotificationMessage(friendly || "Call ended.", "info");
        }
        
        // Transcription disabled
        // stopTranscription();
        break;
      case "failed":
      case "disconnected":
      case "terminated":
        {
          const reason = extractReasonMessage(callData, rawReason);
          const friendly = normalizeUserFacingMessage(reason);
          showNotificationMessage(friendly || "Call did not connect. Please try again.", "error");
          setIsDialing(false);
        }
        break;
      case "holdtoggle":
        handleToggleOnHold();
        break;
      case "mutetoggle":
        handleToggleMute();
        break;
      default:
        break;
    }
  };

  const HandleSessionEvents = useCallback((...args) => {
    // Session updates are optional for current UI; keeping this callback
    // avoids SDK crashes when it emits session state events.
    console.log("[CallControlRibbon] Session event:", ...args);
  }, []);

  const handleExotelAuthFailure = (rawText) => {
    const primaryMessage = normalizeUserFacingMessage(String(rawText || ""));
    setIsExotelAuthInvalid(true);
    setIsDialing(false);
    setIsDeviceRegistered(false);
    showNotificationMessage(primaryMessage || "Your Session Has Expired. Please Login Again.", "error");
  };

  useEffect(() => {
    const exotelPathPattern = /integrationscore\.mum1\.exotel\.com\/v2\/integrations\//i;
    const authPattern = /invalid\s*authorization|expired|not\s*valid|unauthorized/i;

    const isExotelAuthError = (status, bodyText) => {
      if (status === 401) return true;
      return authPattern.test(String(bodyText || ""));
    };

    if (!originalFetchRef.current && typeof window.fetch === "function") {
      originalFetchRef.current = window.fetch.bind(window);
      window.fetch = async (...args) => {
        const response = await originalFetchRef.current(...args);
        try {
          const url = String(args?.[0]?.url || args?.[0] || "");
          if (exotelPathPattern.test(url)) {
            const clone = response.clone();
            const text = await clone.text().catch(() => "");
            if (isExotelAuthError(response.status, text)) {
              handleExotelAuthFailure(text);
            }
          }
        } catch (inspectError) {
          console.warn("[CallControlRibbon] Exotel fetch inspect error:", inspectError);
        }
        return response;
      };
    }

    if (!originalXhrOpenRef.current && window.XMLHttpRequest?.prototype) {
      originalXhrOpenRef.current = window.XMLHttpRequest.prototype.open;
      originalXhrSendRef.current = window.XMLHttpRequest.prototype.send;

      window.XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
        this.__intalks_exotel_url = String(url || "");
        return originalXhrOpenRef.current.call(this, method, url, ...rest);
      };

      window.XMLHttpRequest.prototype.send = function patchedSend(...sendArgs) {
        const onLoadEnd = () => {
          try {
            const url = this.__intalks_exotel_url || "";
            if (!exotelPathPattern.test(url)) return;
            const responseText = String(this.responseText || "");
            if (isExotelAuthError(this.status, responseText)) {
              handleExotelAuthFailure(responseText);
            }
          } catch (inspectError) {
            console.warn("[CallControlRibbon] Exotel XHR inspect error:", inspectError);
          }
        };
        this.addEventListener("loadend", onLoadEnd, { once: true });
        return originalXhrSendRef.current.call(this, ...sendArgs);
      };
    }

    return () => {
      if (originalFetchRef.current) {
        window.fetch = originalFetchRef.current;
        originalFetchRef.current = null;
      }
      if (originalXhrOpenRef.current && originalXhrSendRef.current && window.XMLHttpRequest?.prototype) {
        window.XMLHttpRequest.prototype.open = originalXhrOpenRef.current;
        window.XMLHttpRequest.prototype.send = originalXhrSendRef.current;
        originalXhrOpenRef.current = null;
        originalXhrSendRef.current = null;
      }
    };
  }, []);

  const RegisterationEvent = (event) => {
    console.log("====================================================================");
    console.log("[CallControlRibbon] REGISTRATION EVENT:", event);
    console.log("====================================================================");
    if (event === "registered") {
      console.log("✅ Device registered successfully!");
      setIsDeviceRegistered(true);
      onReady?.();
      showNotificationMessage("You are online for calls.", "success");
    }
    if (event === "unregistered") {
      console.log("❌ Device unregistered!");
      setIsDeviceRegistered(false);
      showNotificationMessage("You appear offline. Calls may not work.", "error");
    }
  };

  useEffect(() => {
    const initializePhone = async () => {
      if (webPhone.current) {
        return;
      }
      
      // Validate credentials
      if (!config?.accessToken || !config?.userId) {
        console.error("[CallControlRibbon] Missing Exotel credentials");
        showNotificationMessage("Call service is not configured. Please sign in again.", "error");
        return;
      }
      
      try {
        console.log("====================================================================");
        console.log("[CallControlRibbon] INITIALIZING EXOTEL SDK");
        console.log("====================================================================");
        console.log("[CallControlRibbon] Creating ExotelCRMWebSDK instance...");
        const crmWebSDK = new ExotelCRMWebSDK(config.accessToken, config.userId, true);
        console.log("[CallControlRibbon] Calling Initialize...");
        const crmWebPhone = await crmWebSDK.Initialize(
          HandleCallEvents,
          RegisterationEvent,
          HandleSessionEvents
        );
        webPhone.current = crmWebPhone;
        console.log("✅ [CallControlRibbon] Exotel SDK initialized successfully!");
        console.log("====================================================================");
      } catch (error) {
        console.error("====================================================================");
        console.error("[CallControlRibbon] INITIALIZATION FAILED:", error);
        console.error("Error details:", JSON.stringify(error, null, 2));
        console.error("====================================================================");
        showNotificationMessage("Could not start call service. Please try again.", "error");
      }
    };

    initializePhone();

    return () => {
      if (callTimer.current) {
        clearInterval(callTimer.current);
      }
      if (notificationTimerRef.current) {
        clearTimeout(notificationTimerRef.current);
        notificationTimerRef.current = null;
      }
      // Clean up webPhone reference
      if (webPhone.current) {
        webPhone.current = null;
      }
    };
  }, [config]);

  // Keyboard shortcuts for accessibility
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only handle shortcuts when ribbon is not minimized and has focus
      if (minimized) return;
      
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      const isShift = e.shiftKey;
      
      if (isCtrlOrCmd) {
        switch(e.key.toLowerCase()) {
          case 'd':
            e.preventDefault();
            if (!isCallActive && !isIncomingCall && !isDialing) {
              dial();
            }
            break;
          case 'm':
            e.preventDefault();
            if (isCallActive) {
              onClickToggleMute();
            }
            break;
          case 'h':
            e.preventDefault();
            if (isCallActive) {
              onClickToggleHold();
            }
            break;
          case 'c':
            e.preventDefault();
            if (isCallActive) {
              hangup();
            }
            break;
          case 'enter':
            e.preventDefault();
            if (e.target.className.includes('ribbon-phone-input')) {
              dial();
            }
            break;
        }
      }
      
      // Space bar to minimize/expand - Prevent during active calls
      if (e.code === 'Space' && e.target === document.body) {
        if (isCallActive || isIncomingCall) {
          return; // Don't allow minimizing during calls
        }
        e.preventDefault();
        setMinimized(!minimized);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [minimized, isCallActive, isIncomingCall, isDialing, phoneNumber]);

  const startCallTimer = () => {
    setCallDuration(0);
    callTimer.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  };

  const stopCallTimer = () => {
    if (callTimer.current) {
      clearInterval(callTimer.current);
      callTimer.current = null;
    }
    setCallDuration(0);
  };

  /**
   * Initialize real-time transcription - DISABLED
   */
  const initializeTranscription_DISABLED = async (callData) => {
    try {
      console.log('[CallControlRibbon] 🎙️ Initializing transcription...');
      
      // Get sessionId from callData (use callSid or callId)
      const sessionId = callData?.callSid || callData?.callId || `temp-${Date.now()}`;
      
      // Get API URL from window global or default
      const apiUrl = window.INTALKSAI_API_URL || 
                     (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
                       ? 'https://api.intalksai.com' 
                       : 'https://api.intalksai.com');
      
      console.log('[CallControlRibbon] Transcription sessionId:', sessionId);
      console.log('[CallControlRibbon] Transcription apiUrl:', apiUrl);
      
      // Create audio capture service instance
      const audioService = new AudioCaptureService(sessionId, apiUrl);
      
      // Set up event handlers
      audioService.onTranscript = (transcript) => {
        console.log('[CallControlRibbon] 📝 Transcript received:', transcript);
        setTranscripts(prev => [...prev, transcript]);
      };
      
      audioService.onSummary = (summary) => {
        console.log('[CallControlRibbon] 📄 Summary received:', summary);
        setCurrentSummary(summary);
      };
      
      audioService.onError = (error) => {
        console.error('[CallControlRibbon] ❌ Transcription error:', error);
        setIsTranscribing(false);
        showNotificationMessage("Live transcription could not start.", "error");
      };
      
      audioService.onStatus = (status) => {
        console.log('[CallControlRibbon] 📊 Transcription status:', status);
        setIsTranscribing(status === 'connected' || status === 'capturing');
      };
      
      // Initialize with webPhone instance
      if (webPhone.current) {
        await audioService.initialize(webPhone.current);
        audioCapture.current = audioService;
        setIsTranscribing(true);
        console.log('[CallControlRibbon] ✅ Transcription initialized successfully');
        showNotificationMessage("Live transcription started.", "success");
      } else {
        throw new Error('WebPhone not initialized');
      }
    } catch (error) {
      console.error('[CallControlRibbon] ❌ Failed to initialize transcription:', error);
      setIsTranscribing(false);
      // Don't show error to user - transcription is optional
      // showNotificationMessage('Failed to start transcription: ' + error.message, 'error');
    }
  };

  /**
   * Stop real-time transcription - DISABLED
   */
  const stopTranscription_DISABLED = () => {
    try {
      console.log('[CallControlRibbon] 🛑 Stopping transcription...');
      
      if (audioCapture.current) {
        audioCapture.current.stop();
        audioCapture.current = null;
      }
      
      setIsTranscribing(false);
      console.log('[CallControlRibbon] ✅ Transcription stopped');
    } catch (error) {
      console.error('[CallControlRibbon] ❌ Error stopping transcription:', error);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const showNotificationMessage = (message, type = "info") => {
    const display = normalizeUserFacingMessage(String(message ?? ""));
    if (!display) return;

    setNotificationMessage(display);
    setNotificationType(type);
    setShowNotification(true);

    // Keep the latest message visible for the full timeout window.
    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
    }

    // Make sure errors are visible even if ribbon is minimized.
    if (minimized && (type === "error" || type === "warning")) {
      setMinimized(false);
    }

    notificationTimerRef.current = setTimeout(() => {
      setShowNotification(false);
      notificationTimerRef.current = null;
    }, 4000);
  };

  const toUserFriendlyRuntimeMessage = (error, fallbackMessage) => {
    const raw = String(error?.message || "").trim();
    if (!raw) {
      return normalizeUserFacingMessage(fallbackMessage || "Something went wrong. Please try again.");
    }

    const knownMap = [
      { test: /network|failed to fetch|load failed|offline/i, message: "Check your internet connection and try again." },
      { test: /timeout|timed out/i, message: "Check your internet connection and try again." },
      { test: /permission|microphone|media_permission_denied|notallowederror/i, message: "Microphone access is required for calls." }
    ];

    const known = knownMap.find((item) => item.test.test(raw));
    if (known) return known.message;

    return normalizeUserFacingMessage(raw || fallbackMessage || "Something went wrong. Please try again.");
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      default: return 'ℹ️';
    }
  };

  // Log call initiation to API - always uses phone from widget input
  const logCallInitiation = async (callSid, widgetPhone, fromNumber, appUserId) => {
    try {
      const logData = {
        apiKey: apiKey,
        event: 'callInitiated',
        data: {
          callSid: callSid,
          callId: callSid, // Also include as callId for compatibility
          phoneNumber: widgetPhone,
          ToNumber: widgetPhone,
          callDirection: 'outbound',
          customerData: {
            phoneNumber: widgetPhone,
            name: customerData?.name || null,
            customerId: customerData?.customerId || null,
            context: customerData?.context || {},
            callType: customerData?.callType || null,
            agentId: appUserId || config?.userId || null,
            agentName: customerData?.agentName || null,
            metadata: customerData?.metadata || {}
          }
        },
        timestamp: new Date().toISOString(),
        domain: window.location.hostname,
        agentUserId: appUserId || config?.userId || null,
        clientName: customerData?.clientName || null
      };

      console.log("[CallControlRibbon] Logging call to API:", {
        apiUrl: apiUrl,
        callSid: callSid,
        phoneNumber: widgetPhone
      });

      const response = await fetch(`${apiUrl}/api/ribbon/log-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log("[CallControlRibbon] ✅ Call logged successfully:", result);
      } else {
        const errorPayload = await response.json().catch(() => ({}));
        const friendlyMessage = userFacingApiMessage(
          response.status,
          errorPayload,
          "Could not save activity details. Your call can continue."
        );
        console.error("[CallControlRibbon] ❌ Failed to log call:", errorPayload);
        showNotificationMessage(friendlyMessage, "warning");
      }
    } catch (error) {
      console.error("[CallControlRibbon] ❌ Error logging call:", error);
      showNotificationMessage("Could not save activity details. Your call can continue.", "warning");
      // Don't throw - call logging is not critical for call functionality
    }
  };

  const ensureMicrophoneAccess = async () => {
    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        return true;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      console.error("[CallControlRibbon] Microphone permission error:", error);
      showNotificationMessage("Microphone access is required for calls.", "error");
      return false;
    }
  };

  const mapReadinessCodeToMessage = (code) => {
    switch (code) {
      case "NOT_SECURE":
        return "A secure (HTTPS) connection is required for microphone access.";
      case "NOT_SUPPORTED":
        return "This browser does not support calling.";
      case "MICROPHONE_PROMPT":
      case "MICROPHONE_UNAVAILABLE":
        return "Microphone access is required for calls.";
      case "MICROPHONE_BLOCKED":
        return "Microphone access is blocked in your browser settings.";
      default:
        return "Call service is not ready. Please try again.";
    }
  };

  const getBrowserPermissionState = async () => {
    try {
      if (!navigator?.permissions?.query) return "unknown";
      const status = await navigator.permissions.query({ name: "microphone" });
      return status?.state || "unknown";
    } catch (_error) {
      return "unknown";
    }
  };

  const checkCallingReadiness = async (callContext = "outgoing", getUserMediaErrorName = null) => {
    if (!apiUrl || !apiKey) {
      return { ok: true, ready: true, code: "SKIPPED", message: null };
    }

    const permissionState = await getBrowserPermissionState();
    const payload = {
      apiKey,
      callContext,
      clientUserAgent: navigator?.userAgent || "",
      isSecureContext: Boolean(window.isSecureContext),
      webrtcSupported: Boolean(window.RTCPeerConnection),
      mediaDevicesSupported: Boolean(navigator?.mediaDevices?.getUserMedia),
      permissionState,
      getUserMediaErrorName
    };

    try {
      const response = await fetch(`${apiUrl}/api/ribbon/browser/calling-readiness`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 401) {
          return {
            ok: false,
            ready: false,
            code: "INVALID_API_KEY",
            message: userFacingApiMessage(
              response.status,
              data,
              "Your Session Has Expired. Please Login Again."
            )
          };
        }
        return {
          ok: false,
          ready: false,
          code: "SERVER_ERROR",
          message: userFacingApiMessage(
            response.status,
            data,
            "Call service is not ready. Please try again."
          )
        };
      }

      return data;
    } catch (error) {
      console.warn("[CallControlRibbon] calling-readiness check failed:", error);
      return {
        ok: false,
        ready: false,
        code: "NETWORK_ERROR",
        message: "Could not verify call readiness. Please try again."
      };
    }
  };

  const dial = async () => {
    if (isExotelAuthInvalid) {
      showNotificationMessage("Your Session Has Expired. Please Login Again.", "error");
      return;
    }

    // Capture the number from the input at click time - never use customerData or stale state
    const numberToDial = String(phoneNumber || '').trim();
    if (!numberToDial || !isPhoneValid(numberToDial)) {
      showNotificationMessage("Please enter a valid phone number.", "error");
      return;
    }

    console.log("[CallControlRibbon] Dialling:", numberToDial);
    console.log("[CallControlRibbon] webPhone.current:", webPhone.current);
    console.log("[CallControlRibbon] config:", config);

    const readinessBeforeMic = await checkCallingReadiness("outgoing");
    if (!readinessBeforeMic?.ready) {
      const blockedMessage = readinessBeforeMic?.message || mapReadinessCodeToMessage(readinessBeforeMic?.code);
      showNotificationMessage(blockedMessage, "error");
      return;
    }

    const micAllowed = await ensureMicrophoneAccess();
    if (!micAllowed) {
      const readinessAfterMicDenied = await checkCallingReadiness("outgoing", "NotAllowedError");
      if (!readinessAfterMicDenied?.ready) {
        const deniedMessage =
          readinessAfterMicDenied?.message || mapReadinessCodeToMessage(readinessAfterMicDenied?.code);
        showNotificationMessage(deniedMessage, "error");
      }
      return;
    }

    setIsDialing(true);
    setIsIncomingCall(false);
    showNotificationMessage(`Dialing ${numberToDial}.`, "info");
    
    if (!webPhone.current) {
      console.error("[CallControlRibbon] Cannot make call - webPhone not initialized");
      showNotificationMessage("Call service is not available.", "error");
      setIsDialing(false);
      return;
    }
    
    // Callback captures numberToDial at dial time - avoids stale closure from customerData
    const dialCallback = (status, response) => {
      console.log("[CallControlRibbon] dialCallback - dialed number was:", numberToDial);
      if (status === "success") {
        setIsExotelAuthInvalid(false);
        const callSid = response.Data?.CallSid;
        const fromNumber = response.Data?.FromNumber;
        const appUserId = response.Data?.AppUserID;
        if (callSid && apiUrl && apiKey) {
          logCallInitiation(callSid, numberToDial, fromNumber, appUserId);
        }
      } else {
        const parseResponseError = () => {
          const raw = response?.Error;
          if (typeof raw === "string") {
            try {
              const parsed = JSON.parse(raw);
              return parsed?.message || parsed?.error || raw;
            } catch (_e) {
              return raw;
            }
          }
          return raw;
        };

        const errorText = String(
          parseResponseError() ||
          response?.description ||
          response?.message ||
          status ||
          "unknown"
        );
        const loweredError = errorText.toLowerCase();
        if (/invalid\s*authorization|expired|not\s*valid|unauthorized/.test(loweredError)) {
          handleExotelAuthFailure(errorText);
          return;
        }
        const formattedError = errorText
          .replace(/^make_call_api_error\s*[:;-]?\s*/i, "")
          .replace(/^error\s*[:;-]?\s*/i, "")
          .trim();
        if (loweredError.includes("media_permission_denied") || loweredError.includes("permission")) {
          showNotificationMessage("Microphone access is required for calls.", "error");
        } else {
          const friendly = normalizeUserFacingMessage(formattedError);
          showNotificationMessage(friendly || "Call did not connect. Please try again.", "error");
        }
      }
    };
    
    try {
      console.log("====================================================================");
      console.log("[CallControlRibbon] CALL DEBUG INFO");
      console.log("====================================================================");
      console.log("[CallControlRibbon] Phone number (from widget input):", numberToDial);
      console.log("[CallControlRibbon] webPhone available:", !!webPhone.current);
      console.log("====================================================================");
      
      webPhone.current.MakeCall(numberToDial, dialCallback);
      
    } catch (error) {
      console.error("====================================================================");
      console.error("[CallControlRibbon] MakeCall ERROR DETAILS:");
      console.error("Error:", error);
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
      if (error.Error) {
        console.error("Error body:", JSON.parse(error.Error));
      }
      console.error("====================================================================");
      const friendlyMessage = toUserFriendlyRuntimeMessage(
        error,
        "Calling could not start. Please try again."
      );
      showNotificationMessage(friendlyMessage, "error");
      setIsDialing(false);
    }
    
    // Reset dialing state after a delay
    setTimeout(() => {
      setIsDialing(false);
    }, 2000);
  };

  const acceptCall = () => {
    console.log('[CallControlRibbon] ✅ Accepting call...');
    console.log('[CallControlRibbon] Current state before accept:', {
      isCallActive,
      isIncomingCall,
      minimized
    });
    
    if (webPhone?.current) {
      try {
        // Don't set state here - let Exotel SDK handle it via "connected" event
        // Just clear incoming state so UI updates immediately
        setIsIncomingCall(false);
        
        // Accept the call - Exotel will fire "connected" event
        webPhone.current.AcceptCall();
        
        console.log('[CallControlRibbon] ✅ AcceptCall() called, waiting for "connected" event...');
        showNotificationMessage("Accepting call…", "info");
      } catch (error) {
        console.error('[CallControlRibbon] ❌ Error accepting call:', error);
        setIsIncomingCall(true); // Restore incoming state on error
        showNotificationMessage("Could not accept the call. Please try again.", "error");
      }
    } else {
      console.error('[CallControlRibbon] ❌ Cannot accept call - webPhone not available');
      showNotificationMessage("Call service is not ready.", "error");
    }
  };

  const hangup = () => {
    if (webPhone.current) {
      webPhone.current.HangupCall();
    }
    if (callTimer.current) {
      clearInterval(callTimer.current);
      callTimer.current = null;
    }
    setCallActive(false);
    setCallDuration(0);
  };

  const debounceClick = (ref, callback, delay = 500) => {
    const now = Date.now();
    if (now - ref.current < delay) return;
    ref.current = now;
    callback();
  };

  const onClickToggleMute = () => {
    debounceClick(lastMuteClick, () => {
      webPhone?.current?.ToggleMute();
    });
  };

  const handleToggleMute = () => {
    setIsMuted((prev) => {
      const newMutedState = !prev;
      showNotificationMessage(newMutedState ? "Call muted." : "Call unmuted.", "info");
      return newMutedState;
    });
  };

  const handleToggleOnHold = () => {
    setIsOnHold((prev) => {
      const newHoldState = !prev;
      showNotificationMessage(newHoldState ? "Call on hold." : "Call resumed.", "info");
      return newHoldState;
    });
  };

  const onClickToggleHold = () => {
    debounceClick(lastHoldClick, () => {
      webPhone?.current?.ToggleHold();
    });
  };

  const sendDTMF = (digit) => {
    webPhone?.current?.SendDTMF(digit.toString());
  };

  // Drag functionality
  const handleDragStart = (e) => {
    // Only allow dragging from the header area
    if (!e.target.closest('.ribbon-header')) return;
    
    setIsDragging(true);
    
    if (e.type === 'mousedown') {
      setDragOffset({
        x: e.clientX,
        y: e.clientY
      });
    } else if (e.type === 'touchstart') {
      const touch = e.touches[0];
      setDragOffset({
        x: touch.clientX,
        y: touch.clientY
      });
    }
    
    e.preventDefault();
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    let clientX, clientY;
    
    if (e.type === 'mousemove') {
      clientX = e.clientX;
      clientY = e.clientY;
    } else if (e.type === 'touchmove') {
      const touch = e.touches[0];
      clientX = touch.clientX;
      clientY = touch.clientY;
    }
    
    // Simple direct movement - no complex bounds checking for now
    const deltaX = clientX - dragOffset.x;
    const deltaY = clientY - dragOffset.y;
    
    setDragPosition({ x: deltaX, y: deltaY });
    e.preventDefault();
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Add event listeners for drag functionality
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchmove', handleDragMove);
      document.addEventListener('touchend', handleDragEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDragMove);
      document.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, dragOffset]);

  // Log credential status
  if (!config?.accessToken || !config?.userId) {
    console.warn("[CallControlRibbon] Missing Exotel credentials");
  }

  return (
    <div 
      ref={ribbonRef}
      className={`call-ribbon call-ribbon-${position} ${minimized ? 'minimized' : ''} ${isDragging ? 'dragging' : ''}`}
      role="complementary"
      aria-label="Call Control Panel"
      aria-live="polite"
      tabIndex={-1}
      style={{
        zIndex: 9999,
        transform: dragPosition.x !== 0 || dragPosition.y !== 0 
          ? `translate(${dragPosition.x}px, ${dragPosition.y}px)` 
          : undefined
      }}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
    >
      {/* Screen Reader Status */}
      <div className="sr-only" aria-live="assertive">
        {isDeviceRegistered ? 'Call service online' : 'Call service offline'}
        {isCallActive ? `, Call active for ${formatDuration(callDuration)}` : ''}
        {isIncomingCall ? ', Incoming call' : ''}
        {isMuted ? ', Call muted' : ''}
        {isOnHold ? ', Call on hold' : ''}
      </div>

      {/* Enhanced Notification Bar */}
      {showNotification && (
        <div className={`ribbon-notification ${notificationType}`} role="alert">
          <span className="notification-icon" aria-hidden="true">{getNotificationIcon(notificationType)}</span>
          {notificationMessage}
        </div>
      )}

        {/* Header Bar - Prominence Style */}
        <div className="ribbon-header">
          <div className="ribbon-title">
            <span className={`status-dot ${isDeviceRegistered ? 'online' : 'offline'}`}></span>
            <span className="title-text">
              {isCallActive ? 'IntalksAI Callhub - Active Call' : 'IntalksAI Callhub'}
            </span>
              <span className="drag-handle" title="Drag to move">⋮⋮</span>
          </div>
          
          {customerData?.name && (
            <div className="customer-info">
              <span className="customer-name">{customerData.name} ({phoneNumber})</span>
            </div>
          )}

          <button 
            className="ribbon-toggle"
            onClick={() => {
              if (isCallActive || isIncomingCall) {
                return; // Prevent minimizing during calls
              }
              setMinimized(!minimized);
            }}
            disabled={isCallActive || isIncomingCall}
            title={minimized ? "Expand" : "Minimize"}
            aria-label={minimized ? "Expand call control panel" : "Minimize call control panel"}
          >
            {minimized ? '▲' : '▼'}
          </button>
        </div>

      {/* Ribbon Content */}
      {!minimized && (
        <div className="ribbon-content">
          {!isDeviceRegistered && (
            <div className="ribbon-status"></div>
          )}
          {(!isDeviceRegistered || !isCallActive || isIncomingCall || isCallActive) && (
            <>
              {/* Call History - show only when phone entered in widget */}
              {!isCallActive && !isIncomingCall && apiUrl && apiKey && (() => {
                const validPhone = phoneNumber && isPhoneValid(phoneNumber);
                return previousSummaries.length > 0 || loadingSummaries || validPhone;
              })() && (
                <div className="ribbon-previous-calls">
                  <div 
                    className="previous-calls-header"
                    onClick={() => setShowPreviousCalls(!showPreviousCalls)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setShowPreviousCalls(!showPreviousCalls)}
                  >
                    <span className="previous-calls-title">
                      {loadingSummaries ? '📋 Call History' : `📋 Call History (${previousSummaries.length})`}
                    </span>
                    <span className="previous-calls-toggle">{showPreviousCalls ? '▼' : '▶'}</span>
                  </div>
                  {showPreviousCalls && (
                    <div className="previous-calls-list">
                      {loadingSummaries ? (
                        <div className="previous-calls-loading">Loading...</div>
                      ) : previousSummaries.length === 0 ? (
                        <div className="previous-calls-loading">No call history found</div>
                      ) : (
                        previousSummaries.map((s, i) => (
                          <div key={s.session_id || i} className="previous-call-item">
                            <div className="previous-call-meta">
                              {s.initiated_at && new Date(s.initiated_at).toLocaleDateString()}
                              {s.duration != null && ` • ${s.duration}s`}
                              {s.sentiment && ` • ${s.sentiment}`}
                            </div>
                            <div className="previous-call-summary">{s.summary_text}</div>
                            {s.key_points && s.key_points.length > 0 && (
                              <ul className="previous-call-points">
                                {s.key_points.slice(0, 3).map((p, j) => (
                                  <li key={j}>{p}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Phone Input */}
              {!isCallActive && !isIncomingCall && (
                <div className="ribbon-input-group">
                  <input
                    className="ribbon-phone-input"
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Enter phone number"
                  />
                  <button
                    className="ribbon-btn"
                    onClick={() => fetchSummariesByPhone(phoneNumber)}
                    disabled={!isPhoneValid(phoneNumber) || !apiUrl || !apiKey || loadingSummaries}
                    title="Look up call summaries for this number"
                    style={{ marginRight: 4 }}
                  >
                    {loadingSummaries ? '…' : '📋'}
                  </button>
                  <button 
                    className={`ribbon-btn ribbon-btn-dial ${isDialing ? 'loading' : ''}`}
                    onClick={dial}
                    disabled={isDialing || !isDeviceRegistered}
                    title={!isDeviceRegistered ? "Connecting to your call service…" : "Dial"}
                  >
                    {isDialing ? 'Dialing...' : '📞 Dial'}
                  </button>
                </div>
              )}

              {/* Incoming Call */}
              {isIncomingCall && (
                <div className="ribbon-incoming">
                  <span className="incoming-text">Incoming call...</span>
                  <button className="ribbon-btn ribbon-btn-accept" onClick={acceptCall}>
                    ✓ Accept
                  </button>
                  <button className="ribbon-btn ribbon-btn-reject" onClick={hangup}>
                    ✗ Reject
                  </button>
                </div>
              )}

              {/* Active Call Controls - Modern Horizontal Style */}
              {isCallActive && (
                <div className="ribbon-controls-modern">
                  <span className="call-timer">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
                      <path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                    </svg>
                    {formatDuration(callDuration)}
                  </span>
                  
                  <button 
                    className={`control-btn ${isMuted ? 'active' : ''}`}
                    onClick={onClickToggleMute}
                    title={isMuted ? "Unmute" : "Mute"}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      {isMuted ? (
                        <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27 6.01 7.3C6 7.46 6 7.73 6 8v6h4v6l2-2h2.73l4.73 4.73L21 19.73l-7-7.01L4.27 3z"/>
                      ) : (
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
                      )}
                    </svg>
                  </button>
                  
                  <button 
                    className={`control-btn ${isOnHold ? 'active' : ''}`}
                    onClick={onClickToggleHold}
                    title={isOnHold ? "Resume" : "Hold"}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      {isOnHold ? (
                        <path d="M8 5v14l11-7z"/>
                      ) : (
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                      )}
                    </svg>
                  </button>

                  <button 
                    className="control-btn"
                    onClick={() => showNotificationMessage("Add participant is not available yet.", "info")}
                    title="Add participant"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14zm-1-9h-2v2h-2v-2h-2V9h2V7h2v2h2v2z"/>
                    </svg>
                  </button>

                  {/* Transcript Toggle Button - DISABLED */}
                  {/* <button 
                    className={`control-btn ${showTranscriptPanel ? 'active' : ''}`}
                    onClick={() => setShowTranscriptPanel(!showTranscriptPanel)}
                    title={showTranscriptPanel ? "Hide Transcript" : "Show Transcript"}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                    </svg>
                    {isTranscribing && <span className="transcribing-indicator" title="Transcribing...">●</span>}
                  </button> */}

                  <button 
                    className="control-btn hangup-btn"
                    onClick={hangup}
                    title="Hang Up"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                    </svg>
                  </button>
                </div>
              )}

              {/* DTMF Keypad - Collapsible */}
              {isCallActive && (
                <div className="ribbon-dtmf">
                  <div className="dtmf-header">
                    <span>DTMF Keypad</span>
                    <button 
                      className="dtmf-toggle"
                      onClick={() => setShowDTMF(!showDTMF)}
                      title="Toggle Keypad"
                    >
                      🔢
                    </button>
                  </div>
                  {showDTMF && (
                    <div className="dtmf-grid">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0, '#'].map((digit) => (
                        <button
                          key={digit}
                          className={`dtmf-btn ${['*', '#'].includes(digit) ? 'special' : ''}`}
                          onClick={() => sendDTMF(digit)}
                        >
                          {digit}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Transcript Panel - DISABLED */}
              {/* {isCallActive && (
                <TranscriptPanel
                  isOpen={showTranscriptPanel}
                  transcript={transcripts}
                  summary={currentSummary}
                  onClose={() => setShowTranscriptPanel(false)}
                />
              )} */}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CallControlRibbon;
